import axiosInstance from "../components/api/axiosInstance";

const BASE = "/role-permission";

/**
 * GET role permissions for a role.
 * Backend may not support GET on /role-permission (405). On 405/404 returns { data: [], fetched: false }.
 * @param {number|string} roleId
 * @returns {Promise<{ data: Array, fetched: boolean }>}
 */
export async function getRolePermissions(roleId) {
  if (roleId == null || roleId === "") return { data: [], fetched: false };
  try {
    const response = await axiosInstance.get(BASE, {
      params: { role_id: roleId },
    });
    const raw = response?.data;
    const arr = Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.data)
        ? raw.data
        : Array.isArray(raw?.role_permissions)
          ? raw.role_permissions
          : [];
    return { data: arr, fetched: true };
  } catch (err) {
    if (err?.response?.status === 405 || err?.response?.status === 404) {
      return { data: [], fetched: false };
    }
    throw err;
  }
}

/**
 * POST /role-permission
 * Create role-permission assignments.
 * Backend expects: { Role_Id, Permission_Ids: [40, 41, ...] } (non-empty array)
 * @param {number} roleId
 * @param {number|number[]} permissionIds - single id or array
 * @returns {Promise<Object>}
 */
export async function createRolePermission(roleId, permissionIds) {
  const ids = Array.isArray(permissionIds) ? permissionIds : [permissionIds];
  const normalized = ids.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0);
  if (normalized.length === 0) {
    return Promise.resolve();
  }
  const response = await axiosInstance.post(BASE, {
    Role_Id: Number(roleId),
    Permission_Ids: normalized,
  });
  return response?.data ?? response;
}

/**
 * PUT /role-permission/:id
 * Update an existing role-permission record.
 * @param {number|string} rolePermissionId
 * @param {number} roleId
 * @param {number} permissionId
 * @returns {Promise<Object>}
 */
export async function updateRolePermission(rolePermissionId, roleId, permissionId) {
  const id = Number(rolePermissionId);
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error("Invalid Role_Permission_Id");
  }
  const response = await axiosInstance.put(`${BASE}/${id}`, {
    Role_Id: Number(roleId),
    Permission_Id: Number(permissionId),
  });
  return response?.data ?? response;
}

/**
 * DELETE /role-permission/:id
 * Remove a role-permission assignment.
 * @param {number|string} rolePermissionId
 * @returns {Promise<void>}
 */
export async function deleteRolePermission(rolePermissionId) {
  const id = Number(rolePermissionId);
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error("Invalid Role_Permission_Id");
  }
  await axiosInstance.delete(`${BASE}/${id}`);
}
